import { useEffect, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FEATURE_LABELS, FEATURE_PARENT, FEATURE_BLOCK_LABEL } from "@/lib/features";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export function ShopPlanEditor({ shopId, onApplied, onLoaded }) {
  const [plans, setPlans] = useState([]);
  const [v, setV] = useState(null);
  const [planId, setPlanId] = useState("");
  const [addons, setAddons] = useState([]);
  const [amount, setAmount] = useState("");
  const [overrides, setOverrides] = useState({});
  const [showFeat, setShowFeat] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!shopId) { return; }
    Promise.all([api.get("/admin/plans"), api.get(`/admin/shops/${shopId}/plan`)]).then(([p, s]) => {
      setPlans(p.data); setV(s.data); setPlanId(s.data.plan_id || ""); setOverrides(s.data.feature_overrides || {}); onLoaded?.(s.data);
      setAddons(s.data.addons.filter((a) => a.active).map((a) => a.id)); setAmount(s.data.amount ?? s.data.suggested_amount ?? "");
    }).catch((e) => toast.error(formatApiError(e)));
  }, [shopId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!v) { return <Loader2 className="h-4 w-4 animate-spin text-green-brand" />; }
  const plan = plans.find((p) => p.id === planId);
  const suggested = (plan?.price || 0) + v.addons.filter((a) => addons.includes(a.id)).reduce((s, a) => s + a.price, 0);
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/shops/${shopId}/plan`, { plan_id: planId || null, addon_ids: addons, amount: amount === "" ? null : Number(String(amount).replace(",", ".")), feature_overrides: overrides });
      setV(data); setOverrides(data.feature_overrides || {}); toast.success("Plano aplicado"); onApplied?.(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <fieldset data-testid="shop-plan-editor" className="rounded-xl border border-green-900/60 bg-green-950/10 p-3 space-y-2">
      <legend className="px-1 text-[11px] font-mono-caps text-green-300">Plano e adicionais</legend>
      <select data-testid="shop-plan-select" value={planId} onChange={(e) => setPlanId(e.target.value)} className="h-9 w-full rounded-md bg-zinc-900 border border-zinc-800 text-sm text-zinc-100 px-2">
        <option value="">Sem plano (módulos manuais)</option>
        {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {brl(p.price)}</option>)}
      </select>
      <div className="flex flex-wrap gap-3">
        {v.addons.map((a) => (
          <label key={a.id} className="flex items-center gap-1.5 text-xs text-zinc-300">
            <input type="checkbox" data-testid={`shop-addon-${a.slug || a.id}`} checked={addons.includes(a.id)} onChange={() => setAddons((x) => (x.includes(a.id) ? x.filter((i) => i !== a.id) : [...x, a.id]))} className="h-3.5 w-3.5 accent-green-600" />
            {a.name} ({brl(a.price)})
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-zinc-400">Mensalidade</span>
        <Input data-testid="shop-plan-amount" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className="h-8 w-28 bg-zinc-900 border-zinc-800" />
        <button type="button" data-testid="shop-plan-use-suggested" onClick={() => setAmount(suggested.toFixed(2))} className="text-[11px] text-green-300 underline">usar plano + adicionais = {brl(suggested)}</button>
        <Button data-testid="shop-plan-apply" type="button" onClick={apply} disabled={busy} className="ml-auto h-8 bg-green-brand hover:bg-green-brandDark text-white font-bold">Aplicar plano</Button>
      </div>
      <p className="text-[11px] text-zinc-500">Trocar o plano liga/desliga os módulos dele (e zera as exceções). Depois, as chaves abaixo viram exceções desta oficina.</p>
      <button type="button" data-testid="shop-plan-features-toggle" onClick={() => setShowFeat(!showFeat)} className="text-[11px] font-bold text-green-300 underline">{showFeat ? "Ocultar" : "Mostrar"} partes</button>
      {showFeat && plan && (
        <div className="space-y-3 border-t border-zinc-800 pt-2">
          {Object.entries(Object.keys(FEATURE_LABELS).reduce((g, k) => { (g[FEATURE_PARENT[k]] ??= []).push(k); return g; }, {})).map(([blk, keys]) => (
          <div key={blk} data-testid={`shop-feat-group-${blk}`} className="space-y-1.5">
          <div className="text-[10px] font-mono-caps text-green-300">{FEATURE_BLOCK_LABEL[blk] || blk}</div>
          {keys.map((k) => { const lbl = FEATURE_LABELS[k];
            const inPlan = !(plan.features || []).length || plan.features.includes(k);
            const forced = overrides[k];
            const effective = forced == null ? inPlan : !!forced;
            return (
              <label key={k} className="flex items-center justify-between gap-2 text-xs text-zinc-300">
                <span>{lbl}</span>
                <span className="flex items-center gap-2">
                  <input type="checkbox" data-testid={`shop-feat-${k}`} checked={effective} onChange={(e) => setOverrides((o) => { const n = { ...o }; if (e.target.checked === inPlan) { delete n[k]; } else { n[k] = e.target.checked; } return n; })} className="h-3.5 w-3.5 accent-green-600" />
                  {forced != null && <span className="text-[10px] font-bold text-amber-300">exceção</span>}
                </span>
              </label>
            );
          })}
          </div>
          ))}
        </div>
      )}
    </fieldset>
  );
}
